// src/contexts/ToolContext.tsx

import React, { createContext, useContext, useState, useCallback } from 'react';
import { produce } from 'immer';
import { Coordinates, DEFAULT_COORDS } from '../widgets/WeatherMapWidget';
import { LightDescription, LightUpdate, DEFAULT_LIGHTS } from '../types/Light';
import { RealtimeClient } from '@theodoreniu/realtime-api-beta';

interface ToolContextType {
    weatherCoordinates: Coordinates;
    weatherMarker: Coordinates | null;
    addWeatherTool: (client: RealtimeClient) => void;
    resetWeatherTool: () => void;
    memoryKeyValue: { [key: string]: any };
    addMemoryKeyValueTool: (client: RealtimeClient) => void;
    resetMemoryKeyValueTool: () => void;
    lightDescriptions: LightDescription[];
    addControlLightsTools: (client: RealtimeClient) => void;
    resetControlLightsTools: () => void;
}

const ToolContext = createContext<ToolContextType | undefined>(undefined);

export const ToolProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

    /**
     * Weather Map Tool
     */
    const [weatherCoordinates, setWeatherCoordinates] = useState<Coordinates>(DEFAULT_COORDS);
    const [weatherMarker, setWeatherMarker] = useState<Coordinates | null>(null);

    const addWeatherTool = (client: RealtimeClient) => {
        client.addTool(
            {
                name: 'get_weather',
                description:
                    'Retrieves the weather for a given lat, lng coordinate pair. Specify a label for the location.',
                parameters: {
                    type: 'object',
                    properties: {
                        lat: {
                            type: 'number',
                            description: 'Latitude',
                        },
                        lng: {
                            type: 'number',
                            description: 'Longitude',
                        },
                        location: {
                            type: 'string',
                            description: 'Name of the location',
                        },
                    },
                    required: ['lat', 'lng', 'location'],
                },
            },
            async ({ lat, lng, location }: { [key: string]: any }) => {
                setWeatherMarker({ lat, lng, location });
                setWeatherCoordinates({ lat, lng, location });
                const result = await fetch(
                    `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=temperature_2m,wind_speed_10m`
                );
                const json = await result.json();
                const temperature = {
                    value: json.current.temperature_2m as number,
                    units: json.current_units.temperature_2m as string,
                };
                const wind_speed = {
                    value: json.current.wind_speed_10m as number,
                    units: json.current_units.wind_speed_10m as string,
                };
                setWeatherMarker({ lat, lng, location, temperature, wind_speed });
                return json;
            }
        );
    };

    const resetWeatherTool = () => {
        setWeatherCoordinates(DEFAULT_COORDS);
        setWeatherMarker(null);
    };

    /**
     * Set Memory Key-Value Tool
     */
    const [memoryKeyValue, setMemoryKeyValue] = useState<{ [key: string]: any }>({});

    const addMemoryKeyValueTool = (client: RealtimeClient) => {
        client.addTool(
            {
                name: 'set_memory',
                description: 'Saves important data about the user into memory.',
                parameters: {
                    type: 'object',
                    properties: {
                        key: {
                            type: 'string',
                            description:
                                'The key of the memory value. Always use lowercase and underscores, no other characters.',
                        },
                        value: {
                            type: 'string',
                            description: 'Value can be anything represented as a string',
                        },
                    },
                    required: ['key', 'value'],
                },
            },
            async ({ key, value }: { [key: string]: any }) => {
                setMemoryKeyValue((memoryKv) => {
                    const newKv = { ...memoryKv };
                    newKv[key] = value;
                    return newKv;
                });
                return { ok: true };
            }
        );
    };

    const resetMemoryKeyValueTool = () => {
        setMemoryKeyValue({});
    };

    /**
     * Control Light Tool
     */
    const [lightDescriptions, setLightDescriptions] = useState<LightDescription[]>(DEFAULT_LIGHTS);

    const updateLightStates = useCallback((updates: LightUpdate[]) => {
        setLightDescriptions(produce(draft => {
            updates.forEach(update => {
                const lightIndex = draft.findIndex(light => light.name === update.name);
                if (lightIndex !== -1) {
                    draft[lightIndex].current_state = {
                        isOn: update.isOn,
                        brightness: update.brightness,
                        color: update.color
                    };
                }
            });
        }));
    }, []);

    const addControlLightsTools = (client: RealtimeClient) => {
        client.addTool(
            {
                name: 'get_lights',
                description:
                    `Returns information about the configuration of lights including their active status in a user's location.  The data will be returned as JSON consisting of the following:
                    {
                        "lights": [
                            {
                                name: string,
                                groups: string[],
                                description: string,
                                maximum_lumens: number,
                                position: {x: number, y: number},
                                current_state: {isOn: boolean, brightness:number, color: string}
                            }
                        ]
                    }
                    `,
                parameters: {
                    type: 'object',
                    properties: {

                    },
                    required: [],
                },
            },
            async () => {
                return DEFAULT_LIGHTS
            }
        );

        client.addTool(
            {
                name: 'control_lights',
                description:
                    `Changes the settings of multiple lights in the user's home simultaneously`,
                parameters: {
                    type: 'object',
                    properties: {
                        lights: {
                            type: 'array',
                            description: 'Array of light configurations to update',
                            items: {
                                type: 'object',
                                properties: {
                                    name: {
                                        type: 'string',
                                        description: 'Name of light',
                                    },
                                    isOn: {
                                        type: 'boolean',
                                        description: 'If the light is switched on or off (i.e. true | false)',
                                    },
                                    brightness: {
                                        type: 'number',
                                        description: 'Brightness of the light as a floating point number (i.e. 0.4)',
                                    },
                                    color: {
                                        type: 'string',
                                        description: 'Color of the light as a hexidecimal color (i.e. #FF0000)',
                                    },
                                },
                                required: ['name', 'isOn', 'brightness', 'color'],
                            },
                        },
                    },
                    required: ['lights'],
                },
            },
            async ({ lights }: { lights: LightUpdate[] }) => {
                updateLightStates(lights);
                const updatedLightNames = lights.map(light => light.name).join(', ');
                return `Successfully updated the following lights: ${updatedLightNames}`;
            }
        );
    };

    const resetControlLightsTools = () => {
        setLightDescriptions(DEFAULT_LIGHTS);
    };

    return (
        <ToolContext.Provider
            value={{
                weatherCoordinates,
                weatherMarker,
                addWeatherTool,
                resetWeatherTool,
                memoryKeyValue,
                addMemoryKeyValueTool,
                resetMemoryKeyValueTool,
                lightDescriptions,
                addControlLightsTools,
                resetControlLightsTools
            }}
        >
            {children}
        </ToolContext.Provider>
    );
};

export const useTool = () => {
    const context = useContext(ToolContext);
    if (context === undefined) {
        throw new Error('useTool must be used within an ToolProvider');
    }
    return context;
};
